import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall } from '../utils/customerUtils'
import { useWebSocket } from '../contexts/WebSocketContext'

interface SMSMessage {
  id: number
  content: string
  direction: 'inbound' | 'outbound'
  status: string
  from_phone: string
  to_phone: string
  created_at: string
  sent_at: string
}

interface Customer {
  id: number
  customer_id: number
  name: string
  email?: string
  phone?: string
  role: 'applicant' | 'co_applicant'
}

interface SMSClientProps {
  dealId: number
  customers: Customer[]
}

const SMSClient: React.FC<SMSClientProps> = ({ dealId, customers }) => {
  const { t } = useTranslation()
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null)
  const [messages, setMessages] = useState<SMSMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasPhoneNumber, setHasPhoneNumber] = useState<boolean>(false)
  const [userPhoneNumber, setUserPhoneNumber] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const selectedCustomerIdRef = useRef<number | null>(null)
  
  // WebSocket connection
  const { socket, isConnected } = useWebSocket()

  // Check if user has an assigned phone number
  useEffect(() => {
    checkUserPhoneNumber()
  }, [])

  // Set first customer as selected by default
  useEffect(() => {
    if (customers.length > 0 && !selectedCustomerId) {
      setSelectedCustomerId(customers[0].customer_id)
    }
  }, [customers])

  // Keep ref in sync with state
  useEffect(() => {
    selectedCustomerIdRef.current = selectedCustomerId
  }, [selectedCustomerId])

  // Fetch messages when customer changes
  useEffect(() => {
    if (selectedCustomerId) {
      fetchMessages()
    }
  }, [selectedCustomerId])

  // WebSocket event listener for real-time messages
  useEffect(() => {
    if (!socket || !isConnected) {
      console.warn('WebSocket not ready for SMSClient')
      return
    }

    console.log('🔌 SMSClient: Attaching WebSocket listener for SMS events')

    const handleNewMessage = (data: any) => {
      console.log('📨 SMSClient: New SMS received via WebSocket:', data)
      
      const currentCustomerId = selectedCustomerIdRef.current
      
      // Check if message is for the currently selected customer
      if (currentCustomerId && data.conversation?.contact_id === currentCustomerId) {
        console.log('✅ SMSClient: Message is for selected customer, appending')
        
        setMessages(prev => {
          // Check if message already exists
          const exists = prev.some(m => m.id === data.message.id)
          if (exists) {
            console.log('⚠️ SMSClient: Message already exists, skipping')
            return prev
          }
          
          // Append new message
          const newMessages = prev.concat(data.message)
          console.log('Current message count:', prev.length, 'New count:', newMessages.length)
          return newMessages
        })
      }
    }

    socket.on('sms:new_message', handleNewMessage)

    return () => {
      console.log('🔌 SMSClient: Cleaning up WebSocket listener')
      socket.off('sms:new_message', handleNewMessage)
    }
  }, [socket, isConnected])

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
  }

  const checkUserPhoneNumber = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/settings/sms', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        const hasTwoWay = data.settings?.two_way_sms_enabled && data.settings?.custom_phone_number
        setHasPhoneNumber(hasTwoWay)
        setUserPhoneNumber(data.settings?.custom_phone_number || data.admin_default_sms_number)
      }
    } catch (error) {
      console.error('Error checking user phone number:', error)
    }
  }

  const fetchMessages = async () => {
    if (!selectedCustomerId) return

    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('token')
      
      const response = await makeApiCall(
        `api/sms/conversations/customer/${selectedCustomerId}/messages?limit=500`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        // Sort messages by created_at in ascending order (oldest first)
        const sortedMessages = (data.messages || []).sort((a: SMSMessage, b: SMSMessage) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
        setMessages(sortedMessages)
      } else {
        console.error('Failed to fetch messages')
        setMessages([])
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
      setError(t('sms.errorFetchingMessages'))
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedCustomerId || sending) return

    const selectedCustomer = customers.find(c => c.customer_id === selectedCustomerId)
    if (!selectedCustomer?.phone) {
      setError(t('sms.noPhoneNumber'))
      return
    }

    try {
      setSending(true)
      setError(null)
      const token = localStorage.getItem('token')

      const response = await makeApiCall(
        `api/sms/conversations/customer/${selectedCustomerId}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: newMessage.trim()
          })
        }
      )

      if (response.ok) {
        setNewMessage('')
        // Fetch messages immediately to show the sent message
        await fetchMessages()
      } else {
        const errorData = await response.json()
        setError(errorData.message || t('sms.errorSendingMessage'))
      }
    } catch (error) {
      console.error('Error sending message:', error)
      setError(t('sms.errorSendingMessage'))
    } finally {
      setSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return t('sms.today')
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('sms.yesterday')
    } else {
      return date.toLocaleDateString()
    }
  }

  const selectedCustomer = customers.find(c => c.customer_id === selectedCustomerId)

  // Group messages by date
  const groupedMessages = messages.reduce((groups: { [key: string]: SMSMessage[] }, message) => {
    const date = new Date(message.created_at).toDateString()
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(message)
    return groups
  }, {})

  return (
    <div className="flex h-[600px] bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden">
      {/* Conversation List - Only show if multiple customers */}
      {customers.length > 1 && (
        <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('sms.conversations')}
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {customers.map((customer) => (
              <button
                key={customer.customer_id}
                onClick={() => setSelectedCustomerId(customer.customer_id)}
                className={`w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                  selectedCustomerId === customer.customer_id
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-gray-900'
                    : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-semibold">
                      {customer.name?.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {customer.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {customer.phone || t('sms.noPhone')}
                    </p>
                    <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                      {customer.role === 'applicant' ? t('sms.applicant') : t('sms.coApplicant')}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-semibold">
                {selectedCustomer?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {selectedCustomer?.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedCustomer?.phone || t('sms.noPhone')}
                </p>
              </div>
            </div>
            {!hasPhoneNumber && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                  {t('sms.readOnlyMode')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <svg className="w-16 h-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm">{t('sms.noMessages')}</p>
            </div>
          ) : (
            Object.keys(groupedMessages).map((dateKey) => (
              <div key={dateKey}>
                {/* Date separator */}
                <div className="flex items-center justify-center my-4">
                  <span className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full">
                    {formatDate(groupedMessages[dateKey][0].created_at)}
                  </span>
                </div>
                
                {/* Messages for this date */}
                {groupedMessages[dateKey].map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'} mb-2`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        message.direction === 'outbound'
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.direction === 'outbound'
                            ? 'text-blue-100'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Error message */}
        {error && (
          <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Input Area */}
        {hasPhoneNumber && selectedCustomer?.phone ? (
          <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-end space-x-2">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('sms.typeMessage')}
                  rows={1}
                  className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                  style={{ minHeight: '40px', maxHeight: '120px' }}
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-full transition-colors disabled:cursor-not-allowed"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
            {userPhoneNumber && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t('sms.sendingFrom')}: {userPhoneNumber}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-sm">
                {!selectedCustomer?.phone 
                  ? t('sms.customerNoPhone')
                  : t('sms.noPhoneNumberAssigned')
                }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SMSClient


